import { useTranslation } from 'react-i18next';
import { cn, Tooltip } from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';

interface FinanceIQKpiCardProps {
  title: string;
  titleTooltip?: string;
  amount: string;
  count: string;
  accentColor: string;
  isEmpty?: boolean;
  onClick?: () => void;
}

const FinanceIQKpiCard = ({
  title,
  titleTooltip,
  amount,
  count,
  accentColor,
  isEmpty,
  onClick,
}: FinanceIQKpiCardProps) => {
  const { t } = useTranslation();
  const isClickable = onClick && !isEmpty;

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      className={cn(
        'flex w-full flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm',
        isClickable &&
          'cursor-pointer transition-all hover:border-blue-300 hover:bg-blue-50 hover:shadow-md active:scale-[0.98]'
      )}
      style={{ borderTopColor: accentColor, borderTopWidth: 3 }}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {titleTooltip && (
          <Tooltip>
            <Tooltip.Trigger asChild>
              <InfoCircleIcon className="h-3 w-3 shrink-0 cursor-pointer text-gray-400 hover:text-blue-500" />
            </Tooltip.Trigger>
            <Tooltip.Content arrow variant="dark" side="top" className="max-w-48 text-center">
              {titleTooltip}
            </Tooltip.Content>
          </Tooltip>
        )}
      </div>
      {isEmpty ? (
        <>
          <p className="text-3xl font-bold text-gray-400">{t('financeiq.kpi.emptyAmount')}</p>
          <p className="text-xs text-gray-400">{t('financeiq.kpi.emptySubtext')}</p>
        </>
      ) : (
        <>
          <p className="text-3xl font-bold">{amount}</p>
          <p className="text-xs text-text-secondary">{count}</p>
        </>
      )}
    </div>
  );
};

export default FinanceIQKpiCard;
